/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {useGetSetting, useUpdateSetting} from '@agent-assist/api-typescript-react-query-hooks';
import {PERMISSIONS} from '@agent-assist/common/lib/admin/rbac';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, {useCallback, useEffect, useState} from 'react';
import {LoadingSpinner} from '../../components/LoadingSpinner';
import {useUserStore} from '../../stores/userStore';

export type OperationType =
  | 'STARTS_WITH_STRING_OPERATOR'
  | 'ENDS_WITH_STRING_OPERATOR'
  | 'CONTAINS_OPERATOR'
  | 'EQUALS_OPERATOR';

export interface FilterItem {
  id: string;
  name: string;
  operation: OperationType;
  value: string;
}

export interface FilterConfig {
  members: FilterItem[];
}

// Legacy interface for backward compatibility
interface LegacyFilterConfig {
  teams: FilterItem[];
  users: FilterItem[];
  agents?: FilterItem[];
}

const generateId = (): string => {
  return Math.random().toString(36).substring(2, 9);
};

export const operationOptions: {value: OperationType; label: string}[] = [
  {value: 'STARTS_WITH_STRING_OPERATOR', label: 'Starts with'},
  {value: 'ENDS_WITH_STRING_OPERATOR', label: 'Ends with'},
  {value: 'CONTAINS_OPERATOR', label: 'Contains'},
  {value: 'EQUALS_OPERATOR', label: 'Equals'},
];

// FilterTable Component
interface FilterTableProps {
  items: FilterItem[];
  isEditing: boolean;
  onChange: (items: FilterItem[]) => void;
  onAdd: () => void;
  onDelete: (id: string) => void;
  validationErrors: {[id: string]: {name: boolean; matchValue: boolean}};
}

const FilterTable: React.FC<FilterTableProps> = ({items, isEditing, onChange, onAdd, onDelete, validationErrors}) => {
  const handleNameChange = (id: string, value: string) => {
    const updatedItems = items.map((item) => (item.id === id ? {...item, name: value} : item));
    onChange(updatedItems);
  };

  const handleOperationChange = (id: string, operation: OperationType) => {
    const updatedItems = items.map((item) => (item.id === id ? {...item, operation} : item));
    onChange(updatedItems);
  };

  const handleValueChange = (id: string, value: string) => {
    const updatedItems = items.map((item) => (item.id === id ? {...item, value} : item));
    onChange(updatedItems);
  };

  const getItemErrors = (id: string) => {
    // Make sure we return a valid error object even if the ID doesn't exist in validationErrors
    return validationErrors[id] || {name: false, matchValue: false};
  };

  return (
    <div className={'mb-6'}>
      <TableContainer component={Paper} sx={{mb: 2}}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Rule</TableCell>
              <TableCell>UserID</TableCell>
              {isEditing && <TableCell width={'50px'}></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isEditing ? 4 : 3} align={'center'}>
                  <Typography variant={'body2'} color={'text.secondary'}>
                    No team members configured
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        size={'small'}
                        value={item.name}
                        onChange={(e) => handleNameChange(item.id, e.target.value)}
                        error={getItemErrors(item.id)?.name}
                        helperText={getItemErrors(item.id)?.name ? 'Name is required' : ''}
                        required
                      />
                    ) : (
                      item.name
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <Select
                        fullWidth
                        size={'small'}
                        value={item.operation}
                        onChange={(e: SelectChangeEvent) =>
                          handleOperationChange(item.id, e.target.value as OperationType)
                        }
                      >
                        {operationOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : (
                      operationOptions.find((option) => option.value === item.operation)?.label
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        size={'small'}
                        value={item.value}
                        onChange={(e) => handleValueChange(item.id, e.target.value)}
                        error={getItemErrors(item.id)?.matchValue}
                        helperText={getItemErrors(item.id)?.matchValue ? 'Match string is required' : ''}
                        required
                      />
                    ) : (
                      item.value
                    )}
                  </TableCell>
                  {isEditing && (
                    <TableCell>
                      <IconButton size={'small'} color={'error'} onClick={() => onDelete(item.id)}>
                        <DeleteIcon fontSize={'small'} />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {isEditing && (
        <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
          <Button startIcon={<AddIcon />} onClick={onAdd} variant={'outlined'} size={'small'} color={'secondary'}>
            Add New
          </Button>
        </Box>
      )}
    </div>
  );
};

// Main Component
export const MyTeamPage = ({setActions}: {setActions: (actions: React.ReactNode) => void}) => {
  const permissionsMap = useUserStore((state) => state.permissionsMap);
  const [isEditing, setEditing] = useState(false);
  // Set initial loading state to true until data is fetched
  const [isLoading, setIsLoading] = useState(true);

  const userSetting = useGetSetting({userKey: 'teamsUsersFilters'});
  const userSettingUpdate = useUpdateSetting();

  // Storage Service with React Query hooks
  const filterConfigStorageService = {
    getFilterConfig: async (): Promise<FilterConfig> => {
      try {
        const response = await userSetting.refetch();
        if (response.data?.userValue) {
          const parsedConfig = JSON.parse(response.data.userValue);

          // Handle backward compatibility with old format
          if (parsedConfig.teams || parsedConfig.users || parsedConfig.agents) {
            const legacyConfig = parsedConfig as LegacyFilterConfig;
            // Combine teams and users (or agents) into a single members array
            return {
              members: [...(legacyConfig.teams || []), ...(legacyConfig.users || []), ...(legacyConfig.agents || [])],
            };
          }

          return parsedConfig as FilterConfig;
        }
      } catch (error) {
        console.error('Error retrieving filter config:', error);
      }
      return {members: []};
    },

    saveFilterConfig: async (config: FilterConfig): Promise<void> => {
      try {
        // Filter out empty rows before saving
        const filteredConfig = {
          members: config.members.filter(
            (member) => member.name && member.name.trim() && member.value && member.value.trim(),
          ),
        };

        const configJson = JSON.stringify(filteredConfig);

        // If setting exists, update it, otherwise create it
        await userSettingUpdate.mutateAsync({
          userKey: 'teamsUsersFilters',
          updateSettingRequestContent: {
            updatedSetting: configJson,
          },
        });
      } catch (error) {
        console.error('Error saving filter config:', error);
      }
    },
  };

  const [filterConfig, setFilterConfig] = useState<FilterConfig>({
    members: [],
  });
  const [validationErrors, setValidationErrors] = useState<{
    members: Record<string, {name: boolean; matchValue: boolean}>;
  }>({
    members: {},
  });

  useEffect(() => {
    // Load data when userSetting is successful or error
    if (userSetting.isSuccess && userSetting.data?.userValue) {
      try {
        const parsedConfig = JSON.parse(userSetting.data.userValue);

        // Handle backward compatibility with old format
        if (parsedConfig.teams || parsedConfig.users || parsedConfig.agents) {
          const legacyConfig = parsedConfig as LegacyFilterConfig;
          setFilterConfig({
            members: [...(legacyConfig.teams || []), ...(legacyConfig.users || []), ...(legacyConfig.agents || [])],
          });
        } else {
          setFilterConfig(parsedConfig as FilterConfig);
        }
      } catch (error) {
        console.error('Error parsing filter config:', error);
      }
      // Set loading to false after data is loaded
      setIsLoading(false);
    } else if (userSetting.isError) {
      // If setting doesn't exist yet, that's fine - we'll create it on save
      console.log('No existing team members filters found');
      // Set loading to false even if there's an error
      setIsLoading(false);
    } else if (userSetting.isLoading) {
      // Keep loading state true while fetching data
      setIsLoading(true);
    }
  }, [userSetting.isSuccess, userSetting.data, userSetting.isError, userSetting.isLoading]);

  const validateForm = useCallback((): boolean => {
    const newErrors = {
      members: {},
    };

    let isValid = true;

    if (filterConfig.members.length === 0) {
      setValidationErrors(newErrors);
      return true;
    }

    filterConfig.members.forEach((member) => {
      const memberErrors = {name: false, matchValue: false};

      // Only validate if the row has been touched (has some input)
      if (member.name || member.value) {
        if (!member.name || !member.name.trim()) {
          memberErrors.name = true;
          isValid = false;
        }

        if (!member.value || !member.value.trim()) {
          memberErrors.matchValue = true;
          isValid = false;
        }
      }

      // @ts-ignore
      newErrors.members[member.id] = memberErrors;
    });

    setValidationErrors(newErrors);
    return isValid;
  }, [filterConfig, setValidationErrors]);

  const handleSave = useCallback(async () => {
    const isValid = validateForm();

    if (!isValid) {
      return;
    }

    setIsLoading(true);

    try {
      // Save the current config using the API
      await filterConfigStorageService.saveFilterConfig(filterConfig);

      // Refetch to get the latest data
      const savedConfig = await filterConfigStorageService.getFilterConfig();

      // Update the state with the filtered data
      setFilterConfig(savedConfig);

      setEditing(false);
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    } catch (error) {
      console.error('Error saving:', error);
      setIsLoading(false);
    }
  }, [validateForm, filterConfig, setIsLoading, setEditing]);

  const handleCancel = useCallback(async () => {
    setEditing(false);

    // Reload the saved config from the API
    try {
      const savedConfig = await filterConfigStorageService.getFilterConfig();
      setFilterConfig(savedConfig);
    } catch (error) {
      console.error('Error loading saved config:', error);
    }

    setValidationErrors({
      members: {},
    });
  }, [setFilterConfig, setValidationErrors, setEditing]);

  const handleAddMember = useCallback(() => {
    const newMember: FilterItem = {
      id: generateId(),
      name: '',
      operation: 'CONTAINS_OPERATOR',
      value: '',
    };
    setFilterConfig((prev) => ({
      ...prev,
      members: [...prev.members, newMember],
    }));
  }, [setFilterConfig]);

  const handleDeleteMember = useCallback(
    (id: string) => {
      setFilterConfig((prev) => ({
        ...prev,
        members: prev.members.filter((member) => member.id !== id),
      }));
    },
    [setFilterConfig],
  );

  const handleMembersChange = useCallback(
    (members: FilterItem[]) => {
      setFilterConfig((prev) => ({...prev, members}));
    },
    [setFilterConfig],
  );

  const handleStartEditing = useCallback(() => {
    // Always add empty row when entering edit mode if there are none
    if (filterConfig.members.length === 0) {
      handleAddMember();
    }

    setValidationErrors({
      members: {},
    });

    setEditing(true);
  }, [filterConfig.members.length, handleAddMember, setValidationErrors, setEditing]);

  useEffect(() => {
    if (isLoading) {
      setActions(null);
      return;
    }

    if (!isEditing) {
      setActions(
        <Button
          variant={'outlined'}
          onClick={handleStartEditing}
          disabled={!permissionsMap[PERMISSIONS.SETTINGS_UPDATE]}
        >
          Edit
        </Button>,
      );
    } else {
      setActions(
        <Stack direction={'row'} spacing={2}>
          <Button variant={'outlined'} onClick={handleCancel} disabled={!permissionsMap[PERMISSIONS.SETTINGS_UPDATE]}>
            Cancel
          </Button>
          <Button variant={'contained'} onClick={handleSave} disabled={!permissionsMap[PERMISSIONS.SETTINGS_UPDATE]}>
            Save
          </Button>
        </Stack>,
      );
    }
  }, [isEditing, isLoading, permissionsMap, handleCancel, handleSave, handleStartEditing, setActions]);

  let content;
  if (isLoading) {
    content = (
      <Box sx={{width: '100%', display: 'flex', justifyContent: 'center', py: 4}}>
        <LoadingSpinner />
      </Box>
    );
  } else {
    content = (
      <div>
        <FilterTable
          items={filterConfig.members}
          isEditing={isEditing}
          onChange={handleMembersChange}
          onAdd={handleAddMember}
          onDelete={handleDeleteMember}
          validationErrors={validationErrors.members}
        />
      </div>
    );
  }

  return content;
};
